/**
 * The L0 KPI strip at the top of `/ai/overview` (dashboard design rules §1):
 * **six** numbers that answer "is anything wrong with the AI estate right now?",
 * each tile a door down to the panel that explains it.
 *
 * ## Why these six, and in this order
 *
 * The board leads on what is actionable, not on the census. The first two tiles
 * are the reason the board exists — the HTTP 429 rate and quota exhaustion are
 * the numbers that are currently wrong in the estate and nobody was told. The
 * census follows. The last two are coverage findings, and they are the ones with
 * the *inverted* polarity: a big number there is bad news.
 *
 * 1. **Worst HTTP 429 rate** — 33.74% on one account on 2026-08-20 (3,712 of
 *    11,003 calls refused). The single most actionable number on the board.
 * 2. **Highest quota utilisation** — 89.55% on `OpenAI.DataZoneStandard.gpt-5.4`
 *    (600 of 670), with the exhausted count as the delta.
 * 3. **AI service accounts** — a lower bound over the subscriptions that
 *    answered, never a census.
 * 4. **Model deployments** — the reading that must survive `null`: Resource Graph
 *    does not reliably index the deployment child type.
 * 5. **Accounts serving nothing** — measured idle accounts, with the unreadable
 *    count stated beside them so the figure stays honest.
 * 6. **Accounts nothing alerts on** — the coverage gap. Zero alert rules
 *    estate-wide is *bad* news, so a full house here is `danger`, never green.
 *
 * ## Two honesty rules do real work in this file
 *
 * - **`null` is neutral, never green, and never zero.** Every field on
 *   `AiKpiSummary` is nullable because each comes from a query that can fail on
 *   its own, and a zero that means "we could not read" is worse than no number at
 *   all. So `formatCount(null)` renders an em dash and no tone helper here can
 *   return `success` for a `null`.
 * - **A measured zero is a finding, and its polarity is per-tile.** `0` deployed
 *   models and `0` tokens served are *bad*; `0` exhausted quotas is *good*. There
 *   is deliberately no shared "tone for a count" helper — see `./status.ts`.
 *
 * The strip only renders when `access_state` is `ok`. When it is not, the page
 * renders the access panel in its place rather than six em dashes: six blank
 * tiles would still imply the numbers were looked for and found empty.
 */

import { Card, Statistic } from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import type { AiCoverage, AiKpiSummary } from "@/lib/api";

import {
  AI_CAPACITY_PATH,
  AI_ESTATE_PATH,
  aiHref,
  COMPOSITION_ANCHOR,
  OBSERVABILITY_ANCHOR,
  QUOTA_ANCHOR,
  THROTTLE_ANCHOR,
} from "./params";
import {
  cautionTone,
  EM_DASH,
  type FigureTone,
  formatCount,
  formatOfTotal,
  formatPercent,
  formatWindow,
  problemTone,
} from "./status";

interface KpiTileProps {
  /** Where this number is explained — a route, optionally with an anchor. */
  to: string;
  tone: FigureTone;
  children: ReactNode;
}

/** One KPI tile: a card that is also a link to its panel (§4.1 — no dead aggregates). */
function KpiTile({ to, tone, children }: KpiTileProps) {
  return (
    <Link to={to} className="block rounded focus-visible:outline-2">
      <Card variant="outline" accent="left" accentTone={tone} interactive>
        <Card.Body>{children}</Card.Body>
      </Card>
    </Link>
  );
}

interface AiKpiStripProps {
  summary: AiKpiSummary;
  coverage: AiCoverage;
  /** Throttle rate at or above which an account counts as throttled. */
  throttleWarningPct: number;
  /** Quota utilisation at or above which a quota counts as at risk. */
  quotaWarningPct: number;
  /** The overview's ISO 8601 window, stated on the tiles it scopes. */
  window: string;
}

export function AiKpiStrip({
  summary,
  coverage,
  throttleWarningPct,
  quotaWarningPct,
  window,
}: AiKpiStripProps) {
  const windowLabel = formatWindow(window);

  /* ── 1. Throttling ─────────────────────────────────────────────────────
     Null means no account's throttle metric could be read: unknown, so
     neutral. A measured 0% is a genuinely clean window and earns green. */
  const throttleTone: FigureTone =
    summary.worst_throttle_pct == null
      ? "neutral"
      : summary.worst_throttle_pct >= throttleWarningPct
        ? "danger"
        : summary.worst_throttle_pct > 0
          ? "warning"
          : "success";

  /* ── 2. Quota ──────────────────────────────────────────────────────────
     The exhausted count leads the tone, because "one quota is at 100%" is a
     harder fact than "the worst is at 89%". Both null → neutral. */
  const quotaTone: FigureTone =
    summary.quotas_exhausted == null && summary.worst_utilisation_pct == null
      ? "neutral"
      : (summary.quotas_exhausted ?? 0) > 0
        ? "danger"
        : summary.worst_utilisation_pct != null && summary.worst_utilisation_pct >= quotaWarningPct
          ? "warning"
          : "success";

  /* ── 4. Deployments ────────────────────────────────────────────────────
     `deployments_total` null is the live-estate case today: Resource Graph
     indexes no deployment rows. It must not read as an estate with nothing
     deployed, so the tone stays neutral and the caption says which it is. */
  const deploymentsTone: FigureTone =
    summary.deployments_total == null
      ? "neutral"
      : summary.deployments_total === 0
        ? "warning"
        : "accent";

  /* ── 5. Idle accounts ──────────────────────────────────────────────────
     More is worse, and a measured zero is good — but only over the accounts
     whose usage could actually be read, which the caption states. */
  const idleTone: FigureTone = cautionTone(summary.accounts_with_zero_tokens);

  /* ── 6. Alerting coverage ──────────────────────────────────────────────
     Inverted polarity. `accounts_without_alerts` counts accounts nothing will
     ever page anyone about, so more is worse and the measured estate-wide
     value is the worst possible one. Never green while it is above zero. */
  const alertingTone: FigureTone = problemTone(summary.accounts_without_alerts);

  const accountsCaption = coverage.is_lower_bound
    ? `A lower bound across ${formatCount(coverage.subscriptions_visible)} subscriptions that answered — the real number is higher`
    : `Across ${formatCount(coverage.subscriptions_visible)} subscriptions, discovered by resource type`;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
      <KpiTile to={aiHref(AI_CAPACITY_PATH, THROTTLE_ANCHOR)} tone={throttleTone}>
        <Statistic
          label="Worst HTTP 429 rate"
          value={formatPercent(summary.worst_throttle_pct)}
          tone={throttleTone}
          size="lg"
          progress={summary.worst_throttle_pct ?? undefined}
          progressTone={throttleTone === "success" ? "success" : "danger"}
          caption={
            summary.worst_throttle_pct == null
              ? "No account's throttling metric could be read — unknown, not clean"
              : `Share of requests refused on rate or quota limits, ${windowLabel} · warns at ${formatPercent(throttleWarningPct)}`
          }
        />
      </KpiTile>

      <KpiTile to={aiHref(AI_CAPACITY_PATH, QUOTA_ANCHOR)} tone={quotaTone}>
        <Statistic
          label="Highest quota utilisation"
          value={formatPercent(summary.worst_utilisation_pct)}
          tone={quotaTone}
          size="lg"
          progress={summary.worst_utilisation_pct ?? undefined}
          progressTone={
            quotaTone === "danger" ? "danger" : quotaTone === "warning" ? "warning" : "success"
          }
          delta={
            summary.quotas_exhausted == null
              ? "exhausted count unknown"
              : `${formatCount(summary.quotas_exhausted)} exhausted`
          }
          caption={
            summary.worst_utilisation_pct == null
              ? "The quota read failed — headroom is unknown, not unlimited"
              : `Point-in-time · at risk from ${formatPercent(quotaWarningPct)}`
          }
        />
      </KpiTile>

      <KpiTile to={aiHref(AI_ESTATE_PATH, COMPOSITION_ANCHOR)} tone="accent">
        <Statistic
          label="AI service accounts"
          value={formatCount(summary.accounts_total)}
          size="lg"
          delta={coverage.is_lower_bound ? "at least" : undefined}
          caption={
            summary.accounts_total == null
              ? "Discovery failed — the estate size is unknown, not zero"
              : accountsCaption
          }
        />
      </KpiTile>

      <KpiTile to={aiHref(AI_ESTATE_PATH, COMPOSITION_ANCHOR)} tone={deploymentsTone}>
        <Statistic
          label="Model deployments"
          value={formatCount(summary.deployments_total)}
          tone={deploymentsTone}
          size="lg"
          caption={
            summary.deployments_total == null
              ? "The deployment list could not be read — unknown, not an estate with nothing deployed"
              : summary.accounts_with_zero_deployments == null
                ? "Counted, but how many accounts have none could not be established"
                : `${formatCount(summary.accounts_with_zero_deployments)} account(s) measured with none at all` +
                  (summary.accounts_deployments_unreadable > 0
                    ? ` · ${formatCount(summary.accounts_deployments_unreadable)} unreadable and not counted as zero`
                    : "")
          }
        />
      </KpiTile>

      <KpiTile to={aiHref(AI_ESTATE_PATH, OBSERVABILITY_ANCHOR)} tone={idleTone}>
        <Statistic
          label="Accounts serving nothing"
          value={
            summary.accounts_with_zero_tokens == null
              ? EM_DASH
              : formatOfTotal(summary.accounts_with_zero_tokens, summary.accounts_total)
          }
          tone={idleTone}
          size="lg"
          caption={
            summary.accounts_with_zero_tokens == null
              ? "No account's usage could be read — unknown, so none is counted as idle"
              : `Measured zero tokens in the ${windowLabel} — provisioned, billing, holding keys` +
                (summary.accounts_usage_unreadable > 0
                  ? ` · ${formatCount(summary.accounts_usage_unreadable)} unreadable and deliberately not counted here`
                  : "")
          }
        />
      </KpiTile>

      <KpiTile to={aiHref(AI_ESTATE_PATH, OBSERVABILITY_ANCHOR)} tone={alertingTone}>
        <Statistic
          label="Accounts nothing alerts on"
          value={
            summary.accounts_without_alerts == null
              ? EM_DASH
              : formatOfTotal(summary.accounts_without_alerts, summary.accounts_total)
          }
          tone={alertingTone}
          size="lg"
          caption={
            summary.accounts_without_alerts == null
              ? "The alert-rule query failed — alerting coverage is unknown, not absent"
              : summary.accounts_without_alerts === 0
                ? "Every account has at least one alert rule scoped at it"
                : "No Azure Monitor alert rule is scoped at these accounts, so nothing will ever page anyone about them" +
                  (summary.accounts_without_diagnostics == null
                    ? ""
                    : ` · ${formatCount(summary.accounts_without_diagnostics)} also emit no diagnostic logs at all`)
          }
        />
      </KpiTile>
    </div>
  );
}
